<template>
    <group-report-template  v-model:current-report-template-id="currentReportTemplateId"
                            v-model:for-group="forGroup"
                            v-model:ran-report="reportSuccessfullySent"
                            v-model:reload-data="reloadTable"
                            v-model:running-errors="errorSending"
                            v-model:running-report="runningReport"
                            v-model:settings="settings"
                            :has-settings="hasManualRunSettings"
                            :hide-title="true"
                            :use-settings-drawer="true"
                            :use-template-cards="true"
                            :use-group-reporting-option="groupReportingEnabled"
                            :on-demand-only="true"
                            :has-automatic-send="true"
                            :has-run-inputs="false"
                            :report-icon="settingsSchema.icon"
                            :schema="settingsSchema"
                            :can-edit="permissions.can('manage_calibration_report')"
                            report-name="calibration-report"
                            v-model:display-section="displaySection"
                            @useStandardReporting="setUseStandardReporting"
    >

        <template   #create-settings>
            <report-settings-panel  v-model="settings"
                                    v-model:interval="interval"
                                    :schema="settingsSchema"
                                    :is-savable="true"
                                    :timezone="timezone"
                                    :context="settingsContext"
            ></report-settings-panel>
        </template>

        <template   #edit-settings>
            <report-settings-panel  v-model="settings"
                                    v-model:interval="interval"
                                    :schema="settingsSchema"
                                    :is-savable="displaySection === 'show-template' || useStandardReporting"
                                    :timezone="timezone"
                                    :context="settingsContext"
            ></report-settings-panel>
        </template>

        <template   #automatic-options>
            <!-- Summary card; its Edit Recipients button opens the picker in a
                 slide-over. -->
            <calibration-subscribers    :selected-users="selectedUsers"
                                        :can-edit="permissions.can('manage_calibration_report')"
                                        @edit="showRecipientPanel = true"
            ></calibration-subscribers>

            <!-- Edit recipients slide-over. -->
            <slide-over-panel v-model:show="showRecipientPanel">
                <template #title>
                    Automatic Delivery recipients
                </template>

                <template #content>
                    <div class="flex flex-col gap-4 pb-8">
                        <p class="m-0 text-sm leading-6 text-gray-600">
                            Choose who receives this report. It is emailed to them
                            automatically at the end of each month.
                        </p>

                        <user-select-input  v-model="selectedUsers"
                                            :users="allUsers"
                                            label="Recipients"
                                            modal-title="Choose recipients"
                                            modal-description="They will be emailed this report automatically at the end of each month. This list is shared by everyone on this site."
                                            choose-label="Choose recipients"
                                            change-label="Change recipients"
                                            empty-summary="No recipients selected"
                                            empty-message="No users are available to receive this report."
                                            noun="recipient"
                                            plural-noun="recipients"
                                            :quick-selects="[{label: 'Site Admins', ids: adminIds}]"
                                            :select-all="false"
                        ></user-select-input>

                        <button v-if="isSaving"
                                type="button"
                                class="inline-flex w-full max-w-[180px] items-center justify-center gap-1.5 rounded-md border border-transparent bg-blue-600 px-3 py-2 text-base font-medium leading-5 text-white opacity-50 cursor-not-allowed">
                            <tabler-icon name="loader" size-class="h-4 w-4 animate-spin mr-2"></tabler-icon>
                            Saving
                        </button>
                        <button v-else
                                type="button"
                                class="inline-flex w-full max-w-[180px] items-center justify-center gap-1.5 rounded-md border border-transparent bg-blue-600 px-3 py-2 text-base font-medium leading-5 text-white transition-colors duration-150 hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 active:bg-blue-600"
                                @click="saveSettings">
                            Save Recipients
                        </button>
                    </div>
                </template>
            </slide-over-panel>


            <notification-modal v-model:is-open="showReportModal" v-model:is-error-modal="reportModalError">
                <template #title-content>
                    {{ modalTitle }}
                </template>

                {{ modalMessage }}
            </notification-modal>
        </template>

        <template   #run-options="props">
            <!-- Nothing to pick: the shared run form renders no inputs, and the
                 page runs the report from its Run Report button (or the run
                 slide-over's footer for a saved template). -->
            <interval-selection :use-meters="false"
                                :footer-mode="props.footerMode ?? 'none'"
                                :is-running="runningReport"
                                :ran-report="reportSuccessfullySent"
                                :running-errors="errorSending ?? []"
                                @run-report="runReport(props.reportTemplateId ?? null)"
            ></interval-selection>
        </template>
    </group-report-template>
</template>

<script>
import _ from 'lodash'
import axios from 'axios'

import {SonicuCoreMixin} from "../../mixins/SonicuCoreMixin";
import {ajaxTableMixin} from "../../mixins/ajaxTableMixin";
import {useGroupReport} from "@/composables/useGroupReport";

import GroupReportTemplate from "./GroupReportTemplate";
import IntervalSelection from "./IntervalSelection";
import ReportSettingsPanel from "./settings/ReportSettingsPanel.vue";
import CalibrationSubscribers from "./settings/CalibrationSubscribers.vue";
import NotificationModal from "@/components/elements/NotificationModal";
import SlideOverPanel from "@/components/elements/SlideOverPanel";
import TablerIcon from "@/components/elements/TablerIcon.vue";
import UserSelectInput from "@/components/elements/UserSelectInput.vue";
import calibrationSchema from "./settings/schemas/calibration";

export default {
    name: "GroupCalibrationReport",
    mixins: [SonicuCoreMixin, ajaxTableMixin],
    components: {
        GroupReportTemplate,
        IntervalSelection,
        ReportSettingsPanel,
        CalibrationSubscribers,
        NotificationModal,
        SlideOverPanel,
        TablerIcon,
        UserSelectInput,
    },
    props: {
        interval: {
            type: String,
            default: 'monthly',
        },
        defaultSettings: {
            type: Object,
            default() {
                return {}
            }
        },
        allUsers: {
            type: [Object, Array],
        },
        subscribedUsers: {
            type: [Array],
        },
        // The site's admins the picker can show, for its "Select site Admins" button.
        adminIds: {
            type: Array,
            default() {
                return []
            },
        },
        timezone: {
            type: String,
            default: 'America/New_York',
        },
    },
    setup(props) {
        const report = useGroupReport(calibrationSchema, {
            interval: props.interval,
            defaultSettings: props.defaultSettings,
            // The report's one setting; the controller stamps the dates itself.
            extraParams: (state) => ({
                includeNearOrExpiredOnly: state.settings.value.includeNearOrExpiredOnly,
            }),
        })

        return {
            ...report,
        }
    },
    data() {
        return {
            selectedUsers: this.subscribedUsers,
            isSaving: false,
            showRecipientPanel: false,

            showReportModal: false,
            reportModalError: false,
            modalMessage: '',
            modalTitle: '',
        }
    },
    methods: {
        // Lifted verbatim from the legacy Reports/CalibrationReport.vue -- both
        // still post to the legacy `reports/calibration-report/update`
        // endpoint, which owns the CalibrationReportUsers company option.
        saveSettings() {
            this.isSaving = true
            let users = []
            Object.keys(this.selectedUsers).forEach(key => {
                users.push(this.selectedUsers[key].toString())
            })
            let formData = {
                subscribed_users: users,
            }
            axios.post(this.company.buildCompanyRoute('reports/calibration-report/update'), formData)
                .then(response => {
                    this.modalMessage = 'Successfully saved report settings.'
                    this.modalTitle = 'Report Saved'
                    this.reportModalError = false
                    this.showReportModal = true
                })
                .catch(error => {
                    this.modalMessage = 'Error saving report settings.'
                    this.modalTitle = 'Error Saving'
                    this.reportModalError = true
                    this.showReportModal = true
                })
                .finally(() => {
                    this.isSaving = false
                })
        },
    },
    computed: {
        settingsSchema() {
            return calibrationSchema
        },
    },
}
</script>
